/** The DEAL merge modal — fold one IOT row into another (E2d-3's missing operator surface).
 *
 * ⭐ WHY IT EXISTS. The Merge button on a record already existed, but it opened the PRE-CUT picker
 * (`MergeIntoDealModal` → `/api/deal-cards/search`), whose universe is `candidate_deals` with
 * `status='open'` plus the frozen Affinity `opportunities` snapshot. Accept sets a candidate to
 * `status='promoted'`, so every committed native deal — 524 of the IOT's 586 rows — was absent from
 * the list, and the only "Project Alamo" on offer was the legacy Affinity row we had passed on. The
 * owner could see both duplicates on screen and could not fold them together (2026-08-28).
 *
 * ⛔ IT IS A THREE-STEP RITUAL, AND THE PREVIEW IS NOT DECORATION. `deal_merge_log` holds ZERO rows in
 * production: the first merge run through this modal is the engine's first real run. So the operator
 * picks a target, READS what would move, decides any field disagreements, and only then commits.
 * Reversible via `deal_merge.undo_merge`, but "reversible" is not a reason to skip looking.
 *
 * ⛔ CONFLICT KEYS ARE THE SERVER'S. `choice_key` comes back from the preview and is sent back
 * verbatim. This file never builds one from `table` + `key` — a second spelling of an identity key is
 * precisely how the `'opp'` typo turned a whole dual-write into a silent no-op.
 *
 * ⛔ AND IT DOES NOT RE-STATE THE AUDIENCE RULE. G12, ruled by the owner 2026-08-28: the read audience
 * UNIONS — everyone who could see either side sees the merged deal. The engine already does that; this
 * modal's job is to SHOW it, including the one case the ruling's plain wording does not cover
 * (`access.loses`: a grant on one side plus an active deny on the other resolves to the deny).
 *
 * ⭐ TWO AI DRAFTS MERGE HERE TOO (owner rulings R1/R2, 2026-10-26 — "Flood Form" / "FloodForm").
 * Standing on a draft, the picker also offers other drafts, labelled "AI proposal" because their stage
 * is proposal content the server never sends. A draft survivor stays a draft — the preview says so.
 * If the drafts disagree on fields this caller may not read, the server refuses with a plain-English
 * 409 naming who can run the merge; `errText` shows it verbatim. Nothing here re-decides any of that.
 *
 * Modal state is `useState` — transient modals are not URL state (frontend rules).
 */
import { useEffect, useMemo, useState } from 'react';
import styles from './crm.module.css';
import { errText } from './CrmMergeModal';
import { ApiError } from '../../api/client';
import {
  useEntryMergePreview,
  useEntryMergeTargets,
  useMergeIntoEntry,
} from '../../hooks/useCrm';
import type { CrmDealMergePreview, CrmDealMergeTarget } from '../../types';

interface Props {
  listId: number;
  /** The row the operator is standing on — always the SOURCE. It is the one that goes away. */
  entryId: number;
  sourceName: string;
  onClose: () => void;
  /** Called after the merge commits, with the survivor's entry id, so the panel can navigate to it. */
  onMerged: (survivorEntryId: number) => void;
}

/** A short, legible rendering of a stored field value. The preview shows values so an operator can
 *  choose between them, and an unformatted `value_json` blob is not a choice anyone can make. */
const showValue = (v: unknown): string => {
  if (v === null || v === undefined || v === '') return '(empty)';
  if (typeof v === 'string') {
    // Values arrive as stored JSON, so a plain string is often itself JSON-encoded.
    try {
      const inner = JSON.parse(v);
      if (inner === null) return '(empty)';
      if (typeof inner !== 'object') return String(inner);
      return JSON.stringify(inner);
    } catch {
      return v;
    }
  }
  if (typeof v === 'object') return JSON.stringify(v);
  return String(v);
};

/** `table.column` → the plain-English noun an operator recognises. Anything unmapped falls through to
 *  the raw key rather than being hidden: an unnamed row count is still a row count, and silently
 *  dropping it would understate what the merge does. */
const ROW_LABELS: Record<string, string> = {
  'deal_event_log.list_entry_id': 'emails and meetings',
  'crm_field_value.target_id': 'field values',
  'crm_field_resolved_value.target_id': 'settled field values',
  'crm_relationship_assertion.to_id': 'relationship facts',
  'crm_content_entitlement.content_id': 'access grants',
  'crm_note.subject_id': 'notes',
  'crm_record_review.entry_id': 'review state',
};

export function CrmDealMergeModal({
  listId, entryId, sourceName, onClose, onMerged,
}: Props) {
  const [q, setQ] = useState('');
  /** Debounced at 250ms, the house figure (`CrmMergeModal`, `CrmRefCellEditor`, `CrmListGrid`).
   *  Without it "wastewater" is ten round-trips against a governed read. */
  const [dq, setDq] = useState('');
  const [survivorId, setSurvivorId] = useState<number | null>(null);
  const [preview, setPreview] = useState<CrmDealMergePreview | null>(null);
  const [choices, setChoices] = useState<Record<string, string>>({});
  const [ack, setAck] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const targets = useEntryMergeTargets(listId, entryId, dq);
  const runPreview = useEntryMergePreview(listId);
  const commit = useMergeIntoEntry(listId);

  useEffect(() => { const t = setTimeout(() => setDq(q), 250); return () => clearTimeout(t); }, [q]);

  const rows: CrmDealMergeTarget[] = targets.data?.targets ?? [];
  const survivor = useMemo(
    () => rows.find((r) => r.entry_id === survivorId) ?? null, [rows, survivorId]);

  // Changing the target invalidates everything downstream of it. Carrying a preview — or worse, a set
  // of conflict CHOICES — across a target change would let the operator decide field disagreements for
  // one pair and commit them against another.
  useEffect(() => { setPreview(null); setChoices({}); setAck(false); setErr(null); }, [survivorId]);

  // ⚠ CLAMP A SELECTION THAT FELL OUT OF THE RESULTS (the defect CrmMergeModal's review finding 10
  // caught): editing the query can drop the chosen row from the new result set while `survivorId`
  // keeps pointing at it, leaving the confirm button naming a deal no longer on screen.
  useEffect(() => {
    if (survivorId == null || !targets.data) return;
    if (!rows.some((r) => r.entry_id === survivorId)) setSurvivorId(null);
  }, [targets.data, rows, survivorId]);

  // ⛔ A REFUSAL CLEARS THE PREVIEW. A preview is the operator's evidence for pressing Merge; one left
  // on screen after a refusal (a second, slower preview request erroring after the first succeeded, or
  // a commit 409 because a record was accepted or the conflicts changed underneath) would keep Apply
  // enabled on facts the server just said no longer hold. Clearing it forces a fresh preview.
  const clearPreview = () => { setPreview(null); setChoices({}); setAck(false); };

  /** The machine code of a 409 refusal (`detail.code`), or null. FastAPI wraps the route's detail as
   *  `{detail: {...}}`, which is what `ApiError.detail` holds. */
  const refusalCode = (e: unknown): string | null => {
    if (!(e instanceof ApiError) || e.status !== 409) return null;
    const outer = e.detail as { detail?: { code?: unknown } } | null;
    const code = outer && typeof outer === 'object' ? outer.detail?.code : undefined;
    return typeof code === 'string' ? code : null;
  };

  const doPreview = () => {
    if (survivorId == null) return;
    setErr(null);
    runPreview.mutate({ entryId, survivorEntryId: survivorId }, {
      onSuccess: (out) => setPreview(out),
      onError: (e) => { clearPreview(); setErr(errText(e, 'Could not preview the merge.')); },
    });
  };

  const conflicts = preview?.conflicts ?? [];
  const undecided = conflicts.filter((c) => !choices[c.choice_key]);
  const loses = preview?.access.loses ?? [];
  const needsAck = loses.length > 0;
  const canApply = !!preview && !!survivor && undecided.length === 0
    && (!needsAck || ack) && !commit.isPending;

  const apply = () => {
    if (!canApply || survivorId == null) return;
    setErr(null);
    commit.mutate({ entryId, survivorEntryId: survivorId, conflictChoices: choices }, {
      onSuccess: () => onMerged(survivorId),
      onError: (e) => {
        if (e instanceof ApiError && e.status === 409) clearPreview();
        const msg = errText(e, 'The merge was refused.');
        // `conflicts` = a field disagreement the preview did not show (an edit landed since). The
        // preview is cleared above, so say what to do next rather than leave a dead end.
        setErr(refusalCode(e) === 'conflicts'
          ? `${msg} Preview again to see the new disagreement.` : msg);
      },
    });
  };

  const rowCounts = Object.entries(preview?.row_counts ?? {})
    .filter(([, n]) => n > 0)
    .sort((a, b) => b[1] - a[1]);

  return (
    <div className={styles.modalBackdrop} onMouseDown={onClose}>
      <div className={styles.mergeCard} role="dialog" aria-modal="true" aria-label="Merge deals"
           onMouseDown={(e) => e.stopPropagation()}>
        <h3 className={styles.modalTitle}>Merge deals — {sourceName}</h3>
        <div className={styles.mergeIntro}>
          Pick the deal that SURVIVES. This row&apos;s emails, meetings, field values, notes and
          access grants move onto it, and this row becomes a redirect to it — permanently, though the
          merge can be reversed from the survivor. Everyone who can see either deal will be able to
          see the merged one. If this row is an AI proposal, another AI proposal for the same company
          can be the survivor too.
        </div>

        <label className={styles.mergeLabel} htmlFor="deal-merge-q">Search deals on this list</label>
        <input
          id="deal-merge-q"
          type="text"
          autoFocus
          className={styles.modalInput}
          placeholder="Company name or codename…"
          value={q}
          onChange={(e) => setQ(e.target.value)}
        />

        <div className={styles.mergeResults}>
          {targets.isLoading && <div className={styles.mergeEmpty}>Searching…</div>}
          {!targets.isLoading && rows.length === 0 && (
            <div className={styles.mergeEmpty}>
              {dq.trim() ? 'No matching deals on this list.' : 'No deals to pick from.'}
            </div>
          )}
          {rows.map((r) => (
            <button
              key={r.entry_id}
              type="button"
              className={r.entry_id === survivorId ? styles.mergeResultOn : styles.mergeResult}
              onClick={() => setSurvivorId(r.entry_id)}
            >
              <span className={styles.mergeResultName}>{r.name}</span>
              <span className={styles.mergeResultMeta}>
                {r.lifecycle_status === 'proposed' ? 'AI proposal' : (r.stage || 'no stage')}
                {' '}· entry {r.entry_id}
              </span>
            </button>
          ))}
        </div>

        {survivor && !preview && (
          <div className={styles.modalActions}>
            <button type="button" className={styles.gridMiniBtn} onClick={doPreview}
                    disabled={runPreview.isPending}>
              {runPreview.isPending ? 'Checking…' : `Preview merge into ${survivor.name}`}
            </button>
          </div>
        )}

        {preview && (
          <div className={styles.mergePreview}>
            <div className={styles.mergeDirection}>
              <span className={styles.mergeDirName}>{preview.source_name}</span>
              {' → '}
              <span className={styles.mergeDirName}>{preview.survivor_name}</span>
            </div>

            {preview.survivor_lifecycle_status === 'proposed' && (
              <div className={styles.mergePreviewLine}>
                The surviving record is still an AI proposal — it stays under review as one record;
                Accept, Pass or Dismiss it later as one.
              </div>
            )}

            {rowCounts.length === 0 && (
              <div className={styles.mergePreviewLine}>
                Nothing to move — this row holds no evidence, values or grants of its own. The merge
                still records the redirect, which is what stops it being re-proposed.
              </div>
            )}
            {rowCounts.map(([k, n]) => (
              <div key={k} className={styles.mergePreviewLine}>
                {n} {ROW_LABELS[k] ?? k}
              </div>
            ))}
            {preview.identity_signals > 0 && (
              <div className={styles.mergePreviewLine}>
                {preview.identity_signals} identity signal(s) — these are what stop the deal being
                proposed again
              </div>
            )}

            {/* G12 made visible. `gains` is the ordinary case and is stated plainly; `loses` is the
                one shape the union does not cover, so it gets a warning and a required tick rather
                than a line in a list. */}
            <div className={styles.mergePreviewLine}>
              {preview.access.gains.length} more {preview.access.gains.length === 1 ? 'person' : 'people'}
              {' '}will be able to see this deal · {preview.access.keeps.length} already can
            </div>
            {needsAck && (
              <div className={styles.mergeWarn}>
                ⚠ {loses.length} {loses.length === 1 ? 'person' : 'people'} who can see the surviving
                deal today will LOSE access, because they are blocked on the other one and a block
                wins. Check this is intended before continuing.
              </div>
            )}

            {conflicts.length > 0 && (
              <fieldset className={styles.mergeRoles}>
                <legend className={styles.mergeLabel}>
                  These fields disagree — choose one value each ({undecided.length} left)
                </legend>
                {conflicts.map((c) => (
                  <div key={c.choice_key} className={styles.mergeRoleNote}>
                    <div className={styles.mergeLabel}>
                      {Object.entries(c.key).map(([k, v]) => `${k}=${String(v)}`).join(' ')}
                    </div>
                    <label className={styles.mergeRole}>
                      <input type="radio" name={`c-${c.choice_key}`}
                             checked={choices[c.choice_key] === 'survivor'}
                             onChange={() => setChoices((p) => ({ ...p, [c.choice_key]: 'survivor' }))} />
                      Keep {preview.survivor_name}: {showValue(c.survivor_value)}
                    </label>
                    <label className={styles.mergeRole}>
                      <input type="radio" name={`c-${c.choice_key}`}
                             checked={choices[c.choice_key] === 'source'}
                             onChange={() => setChoices((p) => ({ ...p, [c.choice_key]: 'source' }))} />
                      Take {preview.source_name}: {showValue(c.source_value)}
                    </label>
                  </div>
                ))}
              </fieldset>
            )}

            {needsAck && (
              <label className={styles.modalCheck}>
                <input type="checkbox" checked={ack} onChange={(e) => setAck(e.target.checked)} />
                I understand some people will lose access to this deal
              </label>
            )}
          </div>
        )}

        {err && <div className={styles.modalError}>{err}</div>}

        <div className={styles.modalActions}>
          <button type="button" className={styles.gridPageBtn} onClick={onClose}
                  disabled={commit.isPending}>
            Cancel
          </button>
          {/* Both names are read back IN the button — the identity-not-id rule PM-9 established after
              two wrong-record merges in one session. */}
          <button type="button" className={styles.gridMiniBtn} onClick={apply} disabled={!canApply}>
            {commit.isPending
              ? 'Merging…'
              : (preview
                ? `Merge ${preview.source_name} into ${preview.survivor_name}`
                : 'Merge')}
          </button>
        </div>
      </div>
    </div>
  );
}
